import { Category } from '../../api/models/category';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import {
  GoToPrevious,
  HideHeaderAndFooter,
  LoadBanners,
  LoadCategories,
  LockBody,
  Redirect,
  SetPostLoginRoute,
  SetPreMobileMenuRoute,
  ShowHeaderAndFooter,
  TriggerServerErrorFlagDone,
  TriggerServerErrorFlagStart,
  UnlockBody
} from './layout.actions';
import { CategoriesService } from '../../api/services/categories.service';
import { Navigate, RouterNavigation } from '@ngxs/router-plugin';
import { Location } from '@angular/common';
import { BannerDto } from '../../api/models/banner-dto';
import { BannersService } from '../../api/services/banners.service';
import { map } from 'rxjs';
import { PlatformService } from '@app/modules/shared/services/platform-service';

export class LayoutStateModel {
  isShowHeaderAndFooter: boolean;
  postLoginRoute: string;
  categories: Category[];
  preMobileMenuRoute: string;
  preRedirectionRoute: string;
  redirectionRoute: string;
  serverErrorAppeared: boolean;
  banners: BannerDto[];
}

@State<LayoutStateModel>({
  name: 'layout',
  defaults: {
    isShowHeaderAndFooter: true,
    postLoginRoute: '/',
    categories: [],
    preMobileMenuRoute: '/',
    preRedirectionRoute: null,
    redirectionRoute: '/',
    serverErrorAppeared: false,
    banners: []
  }
})
@Injectable()
export class LayoutState {
  private store = inject(Store);
  private categoriesService = inject(CategoriesService);
  private location = inject(Location);
  private bannerService = inject(BannersService);
  private platformService = inject(PlatformService);

  @Selector()
  static showHeaderAndFooter(state: LayoutStateModel): boolean {
    return state.isShowHeaderAndFooter;
  }

  @Selector()
  static selectPostLoginRoute(state: LayoutStateModel): string {
    return state.postLoginRoute;
  }

  @Selector()
  static categories(state: LayoutStateModel): Category[] {
    return state.categories;
  }

  @Selector()
  static selectPreMobileMenuRoute(state: LayoutStateModel): string {
    return state.preMobileMenuRoute;
  }

  @Selector()
  static selectPreRedirectionRoute(state: LayoutStateModel): string {
    return state.preRedirectionRoute;
  }

  @Selector()
  static selectRedirectionRoute(state: LayoutStateModel): string {
    return state.redirectionRoute;
  }

  @Selector()
  static selectServerErrorFlag(state: LayoutStateModel): boolean {
    return state.serverErrorAppeared;
  }

  @Selector()
  static selectBanners(state: LayoutStateModel): BannerDto[] {
    return state.banners;
  }

  @Action(RouterNavigation)
  private navigationEnd(ctx: StateContext<LayoutState>, action: RouterNavigation) {
    const url = action.routerState.url;
    const isAuthPage = url.indexOf('auth') !== -1;
    const isPostLoginPage = url.indexOf('post-login') !== -1;
    const isCheckoutPage = url.indexOf('checkout') !== -1;
    const isMobilePage = url.indexOf('mobile') > -1;

    if ((isAuthPage && !isPostLoginPage) || isCheckoutPage || isMobilePage) {
      return this.store.dispatch(new HideHeaderAndFooter());
    }
    return this.store.dispatch(new ShowHeaderAndFooter());
  }

  @Action(LoadCategories)
  private loadCategories({ patchState }: StateContext<LayoutStateModel>) {
    return this.categoriesService.apiProductsqueryCategoriesGet().pipe(
      map((response) => {
        response.forEach((cat) => {
          let brands = [];
          cat.brands.forEach((brand) => {
            if (!brands.find((b) => b.brandIdd === brand.brandId)) brands.push(brand);
          });
          cat.brands = brands;
        });
        patchState({ categories: response });
      })
    );
  }

  @Action(Redirect)
  private redirect({ patchState }: StateContext<LayoutStateModel>, action: Redirect) {
    patchState({
      preRedirectionRoute: action.payload.preRedirectionRoute,
      redirectionRoute: action.payload.redirectionRoute
    });
    return this.store.dispatch(new Navigate([action.payload.redirectionRoute]));
  }

  @Action(SetPreMobileMenuRoute)
  private setPreMobileMenuRoute({ patchState }: StateContext<LayoutStateModel>, action: SetPreMobileMenuRoute) {
    return patchState({ preMobileMenuRoute: action.payload });
  }

  @Action(GoToPrevious)
  private goToPrevious(ctx: StateContext<LayoutStateModel>) {
    if (ctx.getState().preRedirectionRoute != null) {
      ctx.patchState({ preRedirectionRoute: null });
      window?.history.go(-2);
    } else {
      return this.location.back();
    }
  }

  @Action(ShowHeaderAndFooter)
  private showHeaderAndFooter({ patchState }: StateContext<LayoutStateModel>) {
    return patchState({ isShowHeaderAndFooter: true });
  }

  @Action(HideHeaderAndFooter)
  private hideHeaderAndFooter({ patchState }: StateContext<LayoutStateModel>) {
    return patchState({ isShowHeaderAndFooter: false });
  }

  @Action(SetPostLoginRoute)
  private setPostLoginRout({ patchState }: StateContext<LayoutStateModel>, action: SetPostLoginRoute) {
    return patchState({ postLoginRoute: action.payload });
  }

  @Action(TriggerServerErrorFlagStart)
  private triggerServerErrorFlagStart({ patchState }: StateContext<LayoutStateModel>) {
    return patchState({ serverErrorAppeared: false });
  }

  @Action(TriggerServerErrorFlagDone)
  private triggerServerErrorFlagDone({ patchState }: StateContext<LayoutStateModel>) {
    return patchState({ serverErrorAppeared: true });
  }

  @Action(LoadBanners)
  private loadHighlightedBoxDeals({ patchState }: StateContext<LayoutStateModel>) {
    return this.bannerService
      .apiProductsqueryBannersGet()
      .pipe(map((response: BannerDto[]) => patchState({ banners: response })));
  }

  @Action(LockBody)
  private lockBody() {
    if (this.platformService.isBrowser()) {
      document.body.classList.add('overflow-hidden');
    }
  }

  @Action(UnlockBody)
  private unlockBody() {
    if (this.platformService.isBrowser()) {
      document.body.classList.remove('overflow-hidden');
    }
  }
}
